import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Loader2, Search, Trash2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Org = { id: string; name: string; slug: string };
type LookedUpVehicle = {
  registrationNumber: string;
  vin: string | null;
  make: string;
  model: string;
  modelYear: number | null;
  fuelType: string | null;
  transmission: string | null;
  wheelDrive: string | null;
  bodyType: string | null;
  exteriorColor: string | null;
  registrationDate: string | null;
  nextEuControl: string | null;
};
type ImageRow = { url: string; uploading?: boolean; error?: string };

function OptionGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      {label && (
        <p style={{ fontSize: 12, color: "var(--muted-foreground)", marginBottom: 6, fontWeight: 500 }}>
          {label}
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: `1.5px solid ${active ? "var(--primary)" : "var(--border)"}`,
                background: active ? "var(--primary)" : "var(--background)",
                color: active ? "white" : "var(--foreground)",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AdminOpprettAuksjonPage() {
  const navigate = useNavigate();

  // Organization
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);

  // Vehicle lookup
  const [regNumber, setRegNumber] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [vehicle, setVehicle] = useState<LookedUpVehicle | null>(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [modelYear, setModelYear] = useState("");
  const [mileage, setMileage] = useState("");

  // Vehicle condition
  const [numberOfKeys, setNumberOfKeys] = useState("");
  const [numberOfWheelSets, setNumberOfWheelSets] = useState("");
  const [tireCondition, setTireCondition] = useState("");
  const [serviceHistory, setServiceHistory] = useState("");

  // Auction details
  const [description, setDescription] = useState("");
  const [startingBid, setStartingBid] = useState("0");
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupPostalCode, setPickupPostalCode] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [auctionType, setAuctionType] = useState<"regular" | "flash">("regular");
  const [durationMinutes, setDurationMinutes] = useState<number>(1440);

  // Images
  const [images, setImages] = useState<ImageRow[]>([]);

  // Submit state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/api/admin/organizations`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setOrgs(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const filteredOrgs = orgSearch
    ? orgs.filter(
        (o) =>
          o.name.toLowerCase().includes(orgSearch.toLowerCase()) ||
          o.slug.toLowerCase().includes(orgSearch.toLowerCase()),
      )
    : orgs;

  async function lookupVehicle() {
    if (!regNumber.trim()) return;
    setLookingUp(true);
    try {
      const res = await fetch(
        `${API}/api/vehicles/lookup?reg=${encodeURIComponent(regNumber.trim())}`,
        { credentials: "include" },
      );
      if (res.ok) {
        const data = await res.json();
        setVehicle(data);
        setManualEntry(false);
      } else {
        setVehicle(null);
        setManualEntry(true);
      }
    } catch {
      setManualEntry(true);
    } finally {
      setLookingUp(false);
    }
  }

  async function uploadFile(file: File, index: number) {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API}/api/vehicles/images`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { error: data.error ?? "Opplasting feilet" };
    }
    const data = (await res.json()) as { url: string };
    return { url: data.url };
  }

  async function handleFileSelect(files: FileList | null) {
    if (!files) return;
    const fileArray = Array.from(files);
    const startIndex = images.length;

    setImages((prev) => [
      ...prev,
      ...fileArray.map(() => ({ url: "", uploading: true })),
    ]);

    for (let i = 0; i < fileArray.length; i++) {
      const idx = startIndex + i;
      const result = await uploadFile(fileArray[i], idx);
      const uploadedUrl = "url" in result ? (result as { url: string }).url : "";
      const uploadError = "error" in result ? (result as { error: string }).error : undefined;
      setImages((prev) =>
        prev.map((img, j) =>
          j === idx ? { url: uploadedUrl, uploading: false, error: uploadError } : img,
        ),
      );
    }
  }

  function removeImage(index: number) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(andSubmit: boolean) {
    if (!selectedOrg) {
      setError("Velg en organisasjon");
      return;
    }

    const vehicleMake = vehicle?.make || make.trim();
    const vehicleModel = vehicle?.model || model.trim();
    if (!vehicleMake || !vehicleModel) {
      setError("Merke og modell er påkrevd");
      return;
    }

    setSaving(true);
    setError(null);

    const title = `${vehicleMake} ${vehicleModel}${
      (vehicle?.modelYear || modelYear) ? ` ${vehicle?.modelYear || modelYear}` : ""
    }${mileage ? ` – ${Number(mileage).toLocaleString("nb-NO")} km` : ""}`;

    const validImages = images.filter((img) => img.url && !img.uploading && !img.error);

    const technicalData: Record<string, string> = {};
    if (numberOfKeys) technicalData.numberOfKeys = numberOfKeys;
    if (numberOfWheelSets) technicalData.numberOfWheelSets = numberOfWheelSets;
    if (tireCondition) technicalData.tireCondition = tireCondition;
    if (serviceHistory) technicalData.serviceHistory = serviceHistory;

    try {
      const res = await fetch(`${API}/api/admin/auctions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organizationId: selectedOrg.id,
          vehicle: {
            make: vehicleMake,
            model: vehicleModel,
            variant: null,
            registrationNumber: regNumber.trim().toUpperCase().replace(/\s/g, "") || null,
            vin: vehicle?.vin || null,
            modelYear: vehicle?.modelYear || (modelYear ? Number(modelYear) : null),
            fuelType: vehicle?.fuelType || null,
            transmission: vehicle?.transmission || null,
            wheelDrive: vehicle?.wheelDrive || null,
            bodyType: vehicle?.bodyType || null,
            exteriorColor: vehicle?.exteriorColor || null,
            registrationDate: vehicle?.registrationDate
              ? new Date(vehicle.registrationDate).toISOString()
              : null,
            nextEuControl: vehicle?.nextEuControl
              ? new Date(vehicle.nextEuControl).toISOString()
              : null,
            mileage: mileage ? Number(mileage) : null,
            technicalData,
          },
          auction: {
            title,
            description: description.trim() || null,
            pickupAddress: pickupAddress.trim() || null,
            pickupPostalCode: pickupPostalCode.trim() || null,
            pickupCity: pickupCity.trim() || null,
            startingBid: Number(startingBid) || 0,
            minimumIncrement: 1000,
            type: auctionType,
            durationMinutes,
          },
          images: validImages.map((img, i) => ({
            url: img.url,
            thumbnailUrl: null,
            altText: null,
            sortOrder: i,
            isPrimary: i === 0,
          })),
          andSubmit,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          typeof data?.error === "string" ? data.error : "Noe gikk galt",
        );
      }

      const result = await res.json();
      navigate(`/auksjoner/${result.id}`);
    } catch (err: any) {
      setError(err.message || "Noe gikk galt");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px" }}>
      <button
        onClick={() => navigate("/auksjoner")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--muted-foreground)",
          fontSize: 14,
          marginBottom: 16,
          padding: 0,
        }}
      >
        <ArrowLeft size={16} /> Tilbake til auksjoner
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 24 }}>
        Opprett auksjon på vegne av kunde
      </h1>

      {/* Organization selector */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>
          Organisasjon
        </Label>
        {selectedOrg ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              background: "var(--accent)",
              borderRadius: 8,
            }}
          >
            <span style={{ fontWeight: 500 }}>{selectedOrg.name}</span>
            <button
              onClick={() => setSelectedOrg(null)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <Input
              placeholder="Søk etter organisasjon..."
              value={orgSearch}
              onChange={(e) => {
                setOrgSearch(e.target.value);
                setOrgDropdownOpen(true);
              }}
              onFocus={() => setOrgDropdownOpen(true)}
            />
            {orgDropdownOpen && filteredOrgs.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  maxHeight: 240,
                  overflowY: "auto",
                  marginTop: 4,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                {filteredOrgs.slice(0, 20).map((o) => (
                  <button
                    key={o.id}
                    onClick={() => {
                      setSelectedOrg(o);
                      setOrgDropdownOpen(false);
                      setOrgSearch("");
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 14px",
                      background: "none",
                      border: "none",
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{o.name}</span>
                    <span style={{ color: "var(--muted-foreground)", marginLeft: 8, fontSize: 12 }}>
                      {o.slug}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Vehicle lookup */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>
          Kjøretøy
        </Label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <Input
            placeholder="Registreringsnummer"
            value={regNumber}
            onChange={(e) => setRegNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookupVehicle()}
            style={{ flex: 1 }}
          />
          <Button onClick={lookupVehicle} disabled={lookingUp || !regNumber.trim()}>
            {lookingUp ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            <span style={{ marginLeft: 6 }}>Søk</span>
          </Button>
        </div>

        {vehicle && (
          <div
            style={{
              padding: 12,
              background: "var(--accent)",
              borderRadius: 8,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            <strong>
              {vehicle.make} {vehicle.model}
              {vehicle.modelYear ? ` (${vehicle.modelYear})` : ""}
            </strong>
            <br />
            {vehicle.fuelType && <span>Drivstoff: {vehicle.fuelType} &middot; </span>}
            {vehicle.transmission && <span>Gir: {vehicle.transmission} &middot; </span>}
            {vehicle.exteriorColor && <span>Farge: {vehicle.exteriorColor}</span>}
            {vehicle.vin && (
              <>
                <br />
                <span style={{ color: "var(--muted-foreground)" }}>VIN: {vehicle.vin}</span>
              </>
            )}
          </div>
        )}

        {(manualEntry || !vehicle) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <div>
              <Label style={{ fontSize: 12 }}>Merke *</Label>
              <Input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Toyota" />
            </div>
            <div>
              <Label style={{ fontSize: 12 }}>Modell *</Label>
              <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="RAV4" />
            </div>
            <div>
              <Label style={{ fontSize: 12 }}>Årsmodell</Label>
              <Input
                value={modelYear}
                onChange={(e) => setModelYear(e.target.value)}
                placeholder="2022"
                type="number"
              />
            </div>
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <Label style={{ fontSize: 12 }}>Kilometerstand</Label>
          <Input
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            placeholder="80000"
            type="number"
          />
        </div>
      </Card>

      {/* Images */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>
          Bilder
        </Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          {images.map((img, i) => (
            <div
              key={i}
              style={{
                position: "relative",
                width: 80,
                height: 80,
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid var(--border)",
                background: "var(--accent)",
              }}
            >
              {img.uploading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <Loader2 size={16} className="animate-spin" />
                </div>
              ) : img.url ? (
                <img
                  src={img.url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "red", fontSize: 11 }}>
                  Feil
                </div>
              )}
              <button
                onClick={() => removeImage(i)}
                style={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <label
            style={{
              width: 80,
              height: 80,
              borderRadius: 8,
              border: "2px dashed var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Camera size={20} style={{ color: "var(--muted-foreground)" }} />
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </label>
        </div>
      </Card>

      {/* Vehicle condition */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 12, display: "block", fontWeight: 600 }}>
          Tilstand
        </Label>
        <div style={{ display: "grid", gap: 16 }}>
          <OptionGroup
            label="Antall nøkler"
            options={[
              { label: "1", value: "1" },
              { label: "2", value: "2" },
              { label: "3+", value: "3+" },
              { label: "Vet ikke", value: "vet_ikke" },
            ]}
            value={numberOfKeys}
            onChange={setNumberOfKeys}
          />
          <OptionGroup
            label="Antall hjulsett"
            options={[
              { label: "1", value: "1" },
              { label: "2", value: "2" },
              { label: "Vet ikke", value: "vet_ikke" },
            ]}
            value={numberOfWheelSets}
            onChange={setNumberOfWheelSets}
          />
          <OptionGroup
            label="Tilstand på dekk"
            options={[
              { label: "Slitt", value: "slitt" },
              { label: "OK tilstand", value: "ok" },
              { label: "Nye", value: "ny" },
              { label: "Vet ikke", value: "vet_ikke" },
            ]}
            value={tireCondition}
            onChange={setTireCondition}
          />
          <OptionGroup
            label="Servicehistorikk"
            options={[
              { label: "Ja", value: "ja" },
              { label: "Nei", value: "nei" },
              { label: "Vet ikke", value: "vet_ikke" },
            ]}
            value={serviceHistory}
            onChange={setServiceHistory}
          />
        </div>
      </Card>

      {/* Auction details */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 12, display: "block", fontWeight: 600 }}>
          Auksjonsdetaljer
        </Label>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <Label style={{ fontSize: 12 }}>Beskrivelse</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beskriv bilens tilstand, utstyr og annen relevant informasjon..."
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--background)",
                fontSize: 14,
                resize: "vertical",
              }}
            />
          </div>
          <div>
            <Label style={{ fontSize: 12 }}>Startpris (kr)</Label>
            <Input
              value={startingBid}
              onChange={(e) => setStartingBid(e.target.value)}
              placeholder="0"
              type="number"
            />
          </div>
          <div>
            <Label style={{ fontSize: 12, marginBottom: 6, display: "block" }}>Auksjonstype</Label>
            <OptionGroup
              label=""
              options={[
                { label: "Lynauksjon", value: "flash" },
                { label: "Vanlig auksjon", value: "regular" },
              ]}
              value={auctionType}
              onChange={(v) => {
                setAuctionType(v as "regular" | "flash");
                setDurationMinutes(v === "flash" ? 60 : 1440);
              }}
            />
          </div>
          <div>
            <Label style={{ fontSize: 12, marginBottom: 6, display: "block" }}>Varighet</Label>
            <OptionGroup
              label=""
              options={
                auctionType === "flash"
                  ? [
                      { label: "15 min", value: "15" },
                      { label: "1 time", value: "60" },
                      { label: "2 timer", value: "120" },
                    ]
                  : [
                      { label: "24 timer", value: "1440" },
                      { label: "48 timer", value: "2880" },
                      { label: "72 timer", value: "4320" },
                    ]
              }
              value={String(durationMinutes)}
              onChange={(v) => setDurationMinutes(Number(v))}
            />
          </div>
        </div>
      </Card>

      {/* Pickup address */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>
          Hentested
        </Label>
        <div style={{ display: "grid", gap: 8 }}>
          <div>
            <Label style={{ fontSize: 12 }}>Adresse</Label>
            <Input
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              placeholder="Gateadresse"
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8 }}>
            <div>
              <Label style={{ fontSize: 12 }}>Postnummer</Label>
              <Input
                value={pickupPostalCode}
                onChange={(e) => setPickupPostalCode(e.target.value)}
                placeholder="0000"
              />
            </div>
            <div>
              <Label style={{ fontSize: 12 }}>Sted</Label>
              <Input
                value={pickupCity}
                onChange={(e) => setPickupCity(e.target.value)}
                placeholder="Oslo"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "12px 16px",
            background: "hsl(0 80% 95%)",
            color: "hsl(0 80% 40%)",
            borderRadius: 8,
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button variant="outline" onClick={() => handleSubmit(false)} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          Lagre som utkast
        </Button>
        <Button onClick={() => handleSubmit(true)} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          Opprett og send til gjennomgang
        </Button>
      </div>
    </div>
  );
}
